/**
 * The correction state of one run (PredictionObject.Corrections.cs, MeasurementPanelView.
 * Corrections.cs): the AI rows, the active saved version, the live landmark positions, the
 * contour handles (STJ / SOV / ascending aorta), the free margins as drawn, and the geometry
 * derived from them (VBR ring, Root Centroid, re-sliced contours). It produces the corrected
 * measurement rows the prediction renders from and the snapshot a save writes.
 *
 * Not edited here yet (entries are carried through a save): centerline edit points, geometric
 * height handles, free-margin handles, custom metrics.
 */
import * as THREE from 'three';
import { findPoint, measureRing, parseMeasurements, type MeasurementRow } from '@/prediction/measurements';
import { correctionsApi } from './api';
import { constrainHandlesToRing, handlesNormal, initialHandleIndices, projectOntoRail, projectRadialToSurface, sliceContour, tiltNormalFromHandle } from './contours';
import { buildRootCentroid, deformRingThroughNadirs, nadirPlane, warpMarginToCommissures } from './geometry';
import {
  applySnapshot,
  buildSnapshot,
  identityKey,
  rowIdentity,
  slugify,
  toVecMm,
  vecMm,
  type CorrectionEntry,
  type CorrectionSnapshot,
  type CorrectionVersionInfo,
  type Identity,
  type PointEdit,
  type Vec3Mm,
} from './model';

export interface LandmarkDef {
  key: string; // inventory row key
  structure: string;
  name: string;
}

/** The draggable point rows and their measurement identity. */
export const LANDMARKS: LandmarkDef[] = [
  { key: 'LNadir', structure: 'Nadir', name: 'L' },
  { key: 'NNadir', structure: 'Nadir', name: 'N' },
  { key: 'RNadir', structure: 'Nadir', name: 'R' },
  { key: 'RlCommissure', structure: 'Commissure', name: 'RL' },
  { key: 'RnCommissure', structure: 'Commissure', name: 'RN' },
  { key: 'LnCommissure', structure: 'Commissure', name: 'LN' },
  { key: 'LcaCoronaryOstia', structure: 'Ostium', name: 'Left Coronary Ostium' },
  { key: 'RcaCoronaryOstia', structure: 'Ostium', name: 'Right Coronary Ostium' },
  { key: 'CentralCoaptationPoint', structure: 'Coaptation', name: 'Coaptation' },
];
export const LANDMARK_BY_KEY = new Map(LANDMARKS.map((l) => [l.key, l]));
export const NADIR_KEYS = ['LNadir', 'NNadir', 'RNadir'];
export const COMMISSURE_KEYS = ['RlCommissure', 'RnCommissure', 'LnCommissure'];
const MARGIN_STRUCTURES = ['RN_LN_FreeMargin', 'RN_RL_FreeMargin', 'RL_LN_FreeMargin'];
const EDITED_EPSILON_MM = 0.05;

export interface ContourDef {
  rowKey: string; // inventory row
  ringKey: string; // Prediction.rings key (upper case structure)
  prefix: string; // handle name prefix, e.g. STJContourControl
  centerName: string; // centre handle name
  mesh: 'stj' | 'sov' | 'asc';
}

/** Contour rows with the desktop's three edge handles plus the centre handle. */
export const CONTOURS: ContourDef[] = [
  { rowKey: 'StjContour', ringKey: 'STJ', prefix: 'STJContourControl', centerName: 'STJContourCenterlineControl', mesh: 'stj' },
  { rowKey: 'SovContour', ringKey: 'SOV', prefix: 'SOVContourControl', centerName: 'SOVContourCenterlineControl', mesh: 'sov' },
  { rowKey: 'AscAortaContour', ringKey: 'ASCENDINGAORTA', prefix: 'AscAortaContourControl', centerName: 'AscAortaContourCenterlineControl', mesh: 'asc' },
];
export const CONTOUR_BY_ROW = new Map(CONTOURS.map((c) => [c.rowKey, c]));

export type SnapMode = 'volumetric' | 'surface' | 'both' | 'off';

interface Landmark {
  def: LandmarkDef;
  rowIndex: number; // index into aiRows
  identity: Identity;
  frame: number | null;
  ai: Vec3Mm;
  baseline: Vec3Mm; // the loaded version's position (or AI)
  live: Vec3Mm;
}

interface Contour {
  def: ContourDef;
  structure: string; // the file's structure string (e.g. STJ, AscendingAorta)
  frame: number | null;
  aiRing: THREE.Vector3[];
  aiHandles: THREE.Vector3[];
  aiCenter: THREE.Vector3;
  aiNormal: THREE.Vector3;
  aiCenterHandle: THREE.Vector3;
  handles: THREE.Vector3[];
  center: THREE.Vector3;
  normal: THREE.Vector3;
  centerHandle: THREE.Vector3;
  baselineHandles: THREE.Vector3[];
  baselineCenter: THREE.Vector3;
  baselineNormal: THREE.Vector3;
  /** The re-sliced ring; null while the contour is the pipeline's. */
  ring: THREE.Vector3[] | null;
}

export interface CorrectionsSummary {
  versions: CorrectionVersionInfo[];
  activeSlug: string | null;
  activeLabel: string | null;
  dirty: boolean;
  busy: boolean;
  error: string | null;
  /** Why the last contour edit was refused (no surface to slice), or null. */
  notice: string | null;
  editing: Set<string>;
  edited: Set<string>;
  snapMode: SnapMode;
  skipped: number;
}

export type HandleConstraint = 'free' | 'centerline' | 'surface' | 'rail';

export interface ContourHandleSpec {
  key: string;
  position: THREE.Vector3;
  color: [number, number, number];
  kind: 'edge' | 'center';
}

export class Corrections {
  readonly runId: string;
  readonly projectId: string;
  private aiRows: MeasurementRow[];
  private configuration: string | null;
  private landmarks = new Map<string, Landmark>();
  private contours = new Map<string, Contour>(); // by ringKey
  /** Current margins (scene mm) when they differ from the AI rows; baseline = loaded version. */
  private margins = new Map<string, THREE.Vector3[]>();
  private marginBaseline = new Map<string, THREE.Vector3[]>();
  private aiMargins = new Map<string, THREE.Vector3[]>();
  private openEnds = new Map<string, string>();
  /** Derived geometry after a nadir edit. */
  private vbrRefit: { ring: THREE.Vector3[]; center: THREE.Vector3; normal: THREE.Vector3 } | null = null;
  private rootCentroid: THREE.Vector3[] | null = null;
  private loaded: CorrectionSnapshot | null = null;
  private carried: CorrectionEntry[] = [];
  versions: CorrectionVersionInfo[] = [];
  activeSlug: string | null = null;
  dirty = false;
  busy = false;
  error: string | null = null;
  notice: string | null = null;
  editing = new Set<string>();
  snapMode: SnapMode = 'both';
  skippedCount = 0;
  /** The pipeline's primary centerline polyline (scene mm) for the root-centroid rebuild. */
  pipelineCenterline: THREE.Vector3[] | null = null;
  /** The AortaCurve line, the preferred rail of the contour centre handles. */
  aortaCurve: THREE.Vector3[] | null = null;
  /** Meshes to slice for a contour (scene mm), supplied by the session. */
  meshesFor: (mesh: ContourDef['mesh']) => THREE.Mesh[] = () => [];
  /** Surface raycast in scene space (origin, unit direction, max distance) -> hit points. */
  surfaceRaycast: (origin: THREE.Vector3, dir: THREE.Vector3, maxDistance: number) => THREE.Vector3[] = () => [];
  onChange: (() => void) | null = null;

  constructor(runId: string, projectId: string, aiRows: MeasurementRow[], configuration: string | null) {
    this.runId = runId;
    this.projectId = projectId;
    this.aiRows = aiRows;
    this.configuration = configuration;
    const pred = parseMeasurements(aiRows, configuration);
    this.indexLandmarks(aiRows, pred);
    this.indexContours(aiRows, pred);
    this.readMargins(aiRows, pred);
  }

  // ------------------------------------------------------------ setup
  private indexLandmarks(rows: MeasurementRow[], pred: ReturnType<typeof parseMeasurements>): void {
    this.landmarks.clear();
    for (const def of LANDMARKS) {
      const pt = findPoint(pred, def.structure, def.name);
      if (!pt) continue;
      const rowIndex = rows.findIndex((r) => r.Category === 'Point' && r.Structure === def.structure && r.Name === def.name && (r.Frame_Index ?? 0) === pt.frame);
      if (rowIndex < 0) continue;
      const r = rows[rowIndex];
      const ai = { x: r.World_X_mm!, y: r.World_Y_mm!, z: r.World_Z_mm! };
      this.landmarks.set(def.key, { def, rowIndex, identity: rowIdentity(r), frame: r.Frame_Index ?? null, ai, baseline: { ...ai }, live: { ...ai } });
    }
  }

  private indexContours(rows: MeasurementRow[], pred: ReturnType<typeof parseMeasurements>): void {
    this.contours.clear();
    for (const def of CONTOURS) {
      const ring = pred.rings.get(def.ringKey);
      if (!ring || ring.length < 6) continue;
      const row = rows.find((r) => r.Category === 'RingPoint' && r.Structure.toUpperCase() === def.ringKey);
      if (!row) continue;
      const plane = pred.planes.get(row.Structure) ?? pred.planes.get(def.ringKey);
      const m = measureRing(ring);
      const center = plane?.center?.clone() ?? m?.centroid.clone() ?? null;
      const normal = plane?.normal?.clone() ?? m?.normal.clone() ?? null;
      if (!center || !normal) continue;
      const idx = initialHandleIndices(ring.length);
      const handles = idx.map((i) => ring[i].clone());
      const centerHandle = this.railPoint(center) ?? center.clone();
      this.contours.set(def.ringKey, {
        def,
        structure: row.Structure,
        frame: row.Frame_Index ?? null,
        aiRing: ring.map((p) => p.clone()),
        aiHandles: handles.map((p) => p.clone()),
        aiCenter: center.clone(),
        aiNormal: normal.clone(),
        aiCenterHandle: centerHandle.clone(),
        handles: handles.map((p) => p.clone()),
        center: center.clone(),
        normal: normal.clone(),
        centerHandle: centerHandle.clone(),
        baselineHandles: handles.map((p) => p.clone()),
        baselineCenter: center.clone(),
        baselineNormal: normal.clone(),
        ring: null,
      });
    }
  }

  private readMargins(rows: MeasurementRow[], pred: ReturnType<typeof parseMeasurements>): void {
    this.aiMargins.clear();
    this.openEnds.clear();
    for (const s of MARGIN_STRUCTURES) {
      const pts = pred.curves.get(s);
      if (pts && pts.length >= 2) this.aiMargins.set(s, pts.map((p) => p.clone()));
    }
    for (const r of rows) {
      if (r.Category === 'Metric' && r.Name === 'open_end' && typeof r.Value === 'number' && r.Value >= 0.5 && MARGIN_STRUCTURES.includes(r.Structure)) this.openEnds.set(r.Structure, (r.Notes ?? '').trim().toLowerCase());
    }
  }

  summary(): CorrectionsSummary {
    const active = this.versions.find((v) => v.slug === this.activeSlug);
    const edited = new Set<string>();
    for (const k of this.landmarks.keys()) if (this.isEdited(k)) edited.add(k);
    for (const c of this.contours.values()) if (this.isContourEdited(c)) edited.add(c.def.rowKey);
    return {
      versions: this.versions,
      activeSlug: this.activeSlug,
      activeLabel: active?.label ?? null,
      dirty: this.dirty,
      busy: this.busy,
      error: this.error,
      notice: this.notice,
      editing: new Set(this.editing),
      edited,
      snapMode: this.snapMode,
      skipped: this.skippedCount,
    };
  }

  private emit(): void {
    this.onChange?.();
  }

  // ------------------------------------------------------------ versions
  async refreshVersions(): Promise<void> {
    try {
      this.versions = (await correctionsApi.list(this.runId)).versions;
      this.error = null;
    } catch (e) {
      this.error = (e as Error).message;
    }
    this.emit();
  }

  /** On open the newest version loads, as on the desktop (LoadCorrections: newest by updated_at). */
  async loadNewest(): Promise<void> {
    await this.refreshVersions();
    if (this.versions.length) await this.activate(this.versions[0].slug);
  }

  /** Switch to a version (or the AI baseline with null); unsaved edits are dropped. */
  async activate(slug: string | null): Promise<void> {
    this.busy = true;
    this.emit();
    try {
      const snap = slug ? await correctionsApi.get(this.runId, slug) : null;
      this.applyLoaded(snap, slug);
      this.error = null;
    } catch (e) {
      this.error = (e as Error).message;
    }
    this.busy = false;
    this.emit();
  }

  private applyLoaded(snap: CorrectionSnapshot | null, slug: string | null): void {
    this.loaded = snap;
    this.activeSlug = slug;
    this.dirty = false;
    this.notice = null;
    this.vbrRefit = null;
    this.rootCentroid = null;
    this.margins.clear();
    this.marginBaseline.clear();
    for (const l of this.landmarks.values()) {
      l.baseline = { ...l.ai };
      l.live = { ...l.ai };
    }
    for (const c of this.contours.values()) this.restoreContour(c, 'ai');
    const applied = applySnapshot(this.aiRows, snap, this.configuration);
    this.skippedCount = applied.skipped.length;
    this.carried = applied.skipped.map((s) => s.entry);
    for (const l of this.landmarks.values()) {
      const e = applied.applied.get(identityKey(l.identity, l.frame)) ?? applied.applied.get(identityKey(l.identity, null));
      if (e) {
        l.baseline = { ...e.edited };
        l.live = { ...e.edited };
      }
    }
    for (const [s, pts] of applied.margins) {
      this.margins.set(s, pts.map((p) => p.clone()));
      this.marginBaseline.set(s, pts.map((p) => p.clone()));
    }
    if (snap) {
      this.applyContourEntries(snap, applied.applied);
      // handle-style entries of structures this run has no contour for are kept for the save
      const known = new Set([...this.contours.values()].map((c) => c.structure.toLowerCase()));
      for (const e of snap.corrections) {
        if (!e.category && e.structure !== 'CenterLine' && e.structure !== 'Angles' && !known.has(e.structure.toLowerCase())) this.carried.push(e);
      }
      // the desktop refits derived geometry on load unless the version is the baseline
      this.recomputeDerived({ nadirs: true, commissures: !applied.margins.size, coaptation: true });
    }
  }

  /** Load the three edge handles (+ centre) of each contour and re-slice from them. */
  private applyContourEntries(snap: CorrectionSnapshot, applied: Map<string, CorrectionEntry>): void {
    for (const c of this.contours.values()) {
      const s = c.structure.toLowerCase();
      let any = false;
      for (let i = 0; i < 3; i++) {
        const e = snap.corrections.find((x) => !x.category && x.structure.toLowerCase() === s && x.name === `${c.def.prefix}_${i + 1}` && x.kind === 'moved');
        if (!e) continue;
        const original = e.original ? vecMm(e.original) : null;
        if (original && original.distanceTo(c.aiHandles[i]) > 1.0) continue; // drift guard
        c.handles[i] = vecMm(e.edited);
        any = true;
      }
      const centerEntry = applied.get(identityKey({ category: 'Point', structure: c.structure, name: 'center', index: 0 }, c.frame)) ?? applied.get(identityKey({ category: 'Point', structure: c.structure, name: 'center', index: 0 }, null));
      if (centerEntry) {
        c.center = vecMm(centerEntry.edited);
        any = true;
      }
      const centerHandleEntry = snap.corrections.find((x) => !x.category && x.structure.toLowerCase() === s && x.name === c.def.centerName && x.kind === 'moved');
      if (centerHandleEntry) c.centerHandle = vecMm(centerHandleEntry.edited);
      else c.centerHandle = this.railPoint(c.center) ?? c.center.clone();
      if (any) {
        c.normal = handlesNormal(c.handles, c.aiNormal) ?? c.aiNormal.clone();
        this.resliceContour(c, null, false);
      }
      c.baselineHandles = c.handles.map((p) => p.clone());
      c.baselineCenter = c.center.clone();
      c.baselineNormal = c.normal.clone();
    }
  }

  // ------------------------------------------------------------ landmarks
  landmarkKeys(): string[] {
    return [...this.landmarks.keys()];
  }

  hasLandmark(key: string): boolean {
    return this.landmarks.has(key);
  }

  /** Rows with a pencil: landmarks and contours this run has. */
  isEditableRow(key: string): boolean {
    if (this.landmarks.has(key)) return true;
    const def = CONTOUR_BY_ROW.get(key);
    return !!def && this.contours.has(def.ringKey);
  }

  position(key: string): THREE.Vector3 | null {
    const l = this.landmarks.get(key);
    return l ? vecMm(l.live) : null;
  }

  isEdited(key: string): boolean {
    const l = this.landmarks.get(key);
    if (l) return dist(l.live, l.baseline) > EDITED_EPSILON_MM;
    const def = CONTOUR_BY_ROW.get(key);
    const c = def ? this.contours.get(def.ringKey) : undefined;
    return c ? this.isContourEdited(c) : false;
  }

  isMovedFromAi(key: string): boolean {
    const l = this.landmarks.get(key);
    return !!l && dist(l.live, l.ai) > EDITED_EPSILON_MM;
  }

  setEditing(key: string, on: boolean): void {
    if (!this.isEditableRow(key)) return;
    if (on) this.editing.add(key);
    else this.editing.delete(key);
    if (!on) this.notice = null;
    this.emit();
  }

  clearEditing(): void {
    if (this.editing.size === 0) return;
    this.editing.clear();
    this.notice = null;
    this.emit();
  }

  setSnapMode(mode: SnapMode): void {
    this.snapMode = mode;
    this.emit();
  }

  /** Move a landmark (scene mm). `live` = during a drag: only the cheap geometry follows;
   * on release the VBR refit / margin warp / root centroid run and the state turns dirty. */
  moveLandmark(key: string, scene: THREE.Vector3, live: boolean): void {
    const l = this.landmarks.get(key);
    if (!l) return;
    l.live = toVecMm(scene);
    if (!live) {
      this.dirty = true;
      this.recomputeDerived({ nadirs: NADIR_KEYS.includes(key), commissures: COMMISSURE_KEYS.includes(key), coaptation: key === 'CentralCoaptationPoint' || NADIR_KEYS.includes(key) });
    }
    this.emit();
  }

  /** Per-row reset: back to the baseline (the loaded version's position, else the AI). */
  resetLandmark(key: string): void {
    const l = this.landmarks.get(key);
    if (!l) return;
    l.live = { ...l.baseline };
    this.recomputeDerived({ nadirs: NADIR_KEYS.includes(key), commissures: COMMISSURE_KEYS.includes(key), coaptation: true });
    this.emit();
  }

  /** Reset any editable row: landmark, contour or (by structure) a margin. */
  resetRow(key: string): void {
    if (this.landmarks.has(key)) return this.resetLandmark(key);
    const def = CONTOUR_BY_ROW.get(key);
    if (def) return this.resetContour(def.ringKey);
  }

  resetMargin(structure: string): void {
    const base = this.marginBaseline.get(structure);
    if (base) this.margins.set(structure, base.map((p) => p.clone()));
    else this.margins.delete(structure);
    this.emit();
  }

  isMarginEdited(structure: string): boolean {
    const cur = this.margins.get(structure);
    const base = this.marginBaseline.get(structure) ?? this.aiMargins.get(structure);
    if (!cur) return false;
    if (!base || base.length !== cur.length) return true;
    return cur.some((p, i) => p.distanceTo(base[i]) > EDITED_EPSILON_MM);
  }

  // ------------------------------------------------------------ contours
  /** The handle spheres to draw: contours in edit mode show three edge handles and the centre. */
  contourHandleSpecs(): ContourHandleSpec[] {
    const specs: ContourHandleSpec[] = [];
    for (const c of this.contours.values()) {
      if (!this.editing.has(c.def.rowKey)) continue;
      c.handles.forEach((p, i) => specs.push({ key: `contour:${c.def.ringKey}:${i}`, position: p.clone(), color: [1, 1, 1], kind: 'edge' }));
      specs.push({ key: `contour:${c.def.ringKey}:center`, position: c.centerHandle.clone(), color: [0.55, 0.9, 1], kind: 'center' });
    }
    return specs;
  }

  /** What a handle key may do while dragged. */
  constraintFor(key: string): HandleConstraint {
    if (key === 'CentralCoaptationPoint') return 'centerline';
    if (key.startsWith('contour:')) return key.endsWith(':center') ? 'rail' : 'surface';
    return 'free';
  }

  private rail(): THREE.Vector3[] {
    if (this.aortaCurve && this.aortaCurve.length >= 2) return this.aortaCurve;
    return this.rootCentroid ?? this.pipelineCenterline ?? [];
  }

  private railPoint(p: THREE.Vector3): THREE.Vector3 | null {
    return projectOntoRail(this.rail(), p)?.point ?? null;
  }

  private isContourEdited(c: Contour): boolean {
    return c.handles.some((h, i) => h.distanceTo(c.baselineHandles[i]) > EDITED_EPSILON_MM) || c.center.distanceTo(c.baselineCenter) > EDITED_EPSILON_MM;
  }

  private contourAtAi(c: Contour): boolean {
    return c.handles.every((h, i) => h.distanceTo(c.aiHandles[i]) <= EDITED_EPSILON_MM) && c.center.distanceTo(c.aiCenter) <= EDITED_EPSILON_MM;
  }

  private restoreContour(c: Contour, to: 'ai' | 'baseline'): void {
    const h = to === 'ai' ? c.aiHandles : c.baselineHandles;
    c.handles = h.map((p) => p.clone());
    c.center = (to === 'ai' ? c.aiCenter : c.baselineCenter).clone();
    c.normal = (to === 'ai' ? c.aiNormal : c.baselineNormal).clone();
    c.centerHandle = this.railPoint(c.center) ?? c.center.clone();
    if (this.contourAtAi(c)) c.ring = null;
    else this.resliceContour(c, null, false);
  }

  /** RefreshSTJRingFromCurrentPositions + ConstrainSTJControlPointsToRing. False when there is
   * no surface to slice or the plane misses it (the ring is left as drawn). */
  private resliceContour(c: Contour, draggedIndex: number | null, refitAfter: boolean): boolean {
    const meshes = this.meshesFor(c.def.mesh);
    const sliced = meshes.length ? sliceContour(meshes, c.center, c.normal) : null;
    if (!sliced) return false;
    c.ring = sliced.ring;
    const idx = constrainHandlesToRing(c.ring, c.handles, draggedIndex);
    if (idx) c.handles = idx.map((i) => c.ring![i].clone());
    if (refitAfter && idx) {
      const n = handlesNormal(c.handles, c.normal);
      if (n) {
        c.normal = n;
        const again = sliceContour(meshes, c.center, c.normal);
        if (again) {
          c.ring = again.ring;
          const idx2 = constrainHandlesToRing(c.ring, c.handles, draggedIndex);
          if (idx2) c.handles = idx2.map((i) => c.ring![i].clone());
        }
      }
    }
    return true;
  }

  private contourLabel(c: Contour): string {
    return c.def.mesh === 'asc' ? 'Ascending aorta contour' : `${c.def.ringKey} contour`;
  }

  /**
   * Drag a contour handle. An edge handle is projected radially onto the surface and tilts the
   * plane about the centerline (else the plane is re-fitted through the three handles); the
   * centre handle slides the ring rigidly along the rail. The ring is re-sliced each time.
   */
  moveContourHandle(ringKey: string, which: number | 'center', scene: THREE.Vector3, live: boolean): void {
    const c = this.contours.get(ringKey);
    if (!c) return;
    // The desktop re-slices the segmented aorta on every handle move; without that mesh (not
    // shipped, or failed to load) the edit is refused and the reason shown, instead of a handle
    // that moves while the contour stays put.
    if (this.meshesFor(c.def.mesh).length === 0) {
      this.setNotice(`${this.contourLabel(c)}: no aorta surface is loaded, so the contour cannot be re-sliced.`);
      return;
    }
    const railHit = projectOntoRail(this.rail(), c.center);
    const tangent = railHit?.tangent ?? c.normal.clone();
    const before = { center: c.center.clone(), normal: c.normal.clone(), handles: c.handles.map((h) => h.clone()), centerHandle: c.centerHandle.clone(), ring: c.ring };
    let sliced: boolean;
    if (which === 'center') {
      const proj = projectOntoRail(this.rail(), scene);
      if (!proj) {
        this.setNotice(`${this.contourLabel(c)}: the centre handle slides along the aorta centerline, and this run has none.`);
        return;
      }
      const delta = proj.point.clone().sub(c.centerHandle);
      c.center.add(delta);
      c.handles = c.handles.map((h) => h.add(delta));
      // anchor the ring plane on the handle's rail point along the normal
      const off = proj.point.clone().sub(c.center).dot(c.normal);
      if (Math.abs(off) > 1e-6) {
        const shift = c.normal.clone().multiplyScalar(off);
        c.center.add(shift);
        c.handles = c.handles.map((h) => h.add(shift));
      }
      c.centerHandle = proj.point.clone();
      sliced = this.resliceContour(c, null, false);
    } else {
      const fallbackDir = c.handles[which].clone().sub(c.center);
      const projected = projectRadialToSurface(c.center, scene, fallbackDir.lengthSq() > 1e-8 ? fallbackDir : new THREE.Vector3(1, 0, 0), (o, d, max) => this.surfaceRaycast(o, d, max));
      c.handles[which] = projected;
      const tilted = tiltNormalFromHandle(c.center, c.normal, tangent, projected);
      if (tilted) c.normal = tilted;
      else c.normal = handlesNormal(c.handles, c.normal) ?? c.normal;
      sliced = this.resliceContour(c, which, !tilted);
      c.centerHandle = this.railPoint(c.center) ?? c.center.clone();
    }
    if (!sliced) {
      // the plane missed the surface: keep the previous contour rather than a floating handle
      c.center = before.center;
      c.normal = before.normal;
      c.handles = before.handles;
      c.centerHandle = before.centerHandle;
      c.ring = before.ring;
      this.setNotice(`${this.contourLabel(c)}: the plane through that position misses the aorta surface.`);
      return;
    }
    this.notice = null;
    if (!live) {
      this.dirty = true;
      if (ringKey === 'STJ') this.recomputeDerived({ nadirs: false, commissures: false, coaptation: true });
    }
    this.emit();
  }

  private setNotice(text: string): void {
    if (this.notice === text) return;
    this.notice = text;
    this.emit();
  }

  resetContour(ringKey: string): void {
    const c = this.contours.get(ringKey);
    if (!c) return;
    this.notice = null;
    this.restoreContour(c, 'baseline');
    if (ringKey === 'STJ') this.recomputeDerived({ nadirs: false, commissures: false, coaptation: true });
    this.emit();
  }

  // ------------------------------------------------------------ derived geometry
  private nadirsAtAi(): boolean {
    return NADIR_KEYS.every((k) => !this.isMovedFromAi(k));
  }

  private stjCenterNow(pred: ReturnType<typeof parseMeasurements>): THREE.Vector3 | null {
    const c = this.contours.get('STJ');
    if (c?.ring) return measureRing(c.ring)?.centroid ?? c.center;
    return pred.planes.get('STJ')?.center ?? findPoint(pred, 'STJ', 'center')?.position ?? null;
  }

  private recomputeDerived(what: { nadirs: boolean; commissures: boolean; coaptation: boolean }): void {
    const pred = parseMeasurements(this.aiRows, this.configuration);
    const L = this.position('LNadir');
    const R = this.position('RNadir');
    const N = this.position('NNadir');
    if (what.nadirs) {
      if (this.nadirsAtAi() || !L || !R || !N) this.vbrRefit = null;
      else {
        const prior = pred.planes.get('VBR')?.normal ?? null;
        const plane = nadirPlane(L, R, N, prior);
        const base = pred.rings.get('VBR');
        const centerHint = pred.planes.get('VBR')?.center ?? null;
        const fit = plane && base ? deformRingThroughNadirs(base, L, R, N, centerHint) : null;
        this.vbrRefit = fit ? { ring: fit.ring, center: fit.center, normal: plane!.normal.dot(fit.normal) < 0 ? fit.normal.negate() : fit.normal } : null;
      }
    }
    if (what.commissures) {
      const rn = this.position('RnCommissure');
      const rl = this.position('RlCommissure');
      const ln = this.position('LnCommissure');
      if (rn && rl && ln) {
        for (const s of MARGIN_STRUCTURES) {
          const cur = this.margins.get(s) ?? this.aiMargins.get(s);
          if (!cur) continue;
          const open = this.openEnds.get(s) ?? '';
          const w = warpMarginToCommissures(cur, [rn, rl, ln], open === 'both' || open === 'start', open === 'both' || open === 'end');
          if (w.moved) this.margins.set(s, w.points);
        }
      }
    }
    if (what.coaptation || what.nadirs) {
      // SyncRootCentroidWithVbr: the pipeline line when the nadirs are the AI's, else rebuilt
      const coapt = this.position('CentralCoaptationPoint');
      const stjC = this.contours.get('STJ');
      const moved = !this.nadirsAtAi() || this.isMovedFromAi('CentralCoaptationPoint') || COMMISSURE_KEYS.some((k) => this.isMovedFromAi(k)) || (!!stjC && !this.contourAtAi(stjC));
      if (!moved || !coapt) this.rootCentroid = null;
      else {
        const vbrPoint = this.vbrRefit?.center ?? pred.planes.get('VBR')?.center ?? null;
        const vbrNormal = this.vbrRefit?.normal ?? pred.planes.get('VBR')?.normal ?? null;
        const rn = this.position('RnCommissure');
        const rl = this.position('RlCommissure');
        const ln = this.position('LnCommissure');
        const line = vbrPoint && vbrNormal ? buildRootCentroid(vbrPoint, vbrNormal, coapt, rn && rl && ln ? [rn, rl, ln] : null, this.stjCenterNow(pred)) : null;
        this.rootCentroid = line?.points ?? null;
      }
    }
  }

  /** Root Centroid polyline to draw (scene mm), or null to use the pipeline's. */
  centerlineOverride(): THREE.Vector3[] | null {
    return this.rootCentroid;
  }

  /** Current VBR plane (scene mm) after a nadir refit, else null. */
  vbrPlaneOverride(): { center: THREE.Vector3; normal: THREE.Vector3 } | null {
    return this.vbrRefit ? { center: this.vbrRefit.center, normal: this.vbrRefit.normal } : null;
  }

  // ------------------------------------------------------------ corrected rows
  /** Replace a structure's ring, plane and centre rows with a new ring (its metrics are
   * dropped so the renderer measures the geometry). */
  private replaceRing(out: MeasurementRow[], structure: string, frame: number | null, ring: THREE.Vector3[], center: THREE.Vector3, normal: THREE.Vector3): MeasurementRow[] {
    const upper = structure.toUpperCase();
    const template = out.find((r) => r.Category === 'RingPoint' && r.Structure.toUpperCase() === upper);
    const rows = out.filter((r) => !(r.Structure.toUpperCase() === upper && (r.Category === 'RingPoint' || r.Category === 'Diameter' || r.Category === 'Metric' || r.Category === 'Plane' || (r.Category === 'Point' && r.Name === 'center'))));
    const c = toVecMm(center);
    const n = toVecMm(normal);
    const base = template ?? ({ Category: 'RingPoint', Structure: structure, Name: '', Index: null, Pair: '', Units: 'mm', Source: '', Notes: '' } as MeasurementRow);
    ring.forEach((p, i) => {
      const v = toVecMm(p);
      rows.push({ ...base, Category: 'RingPoint', Name: '', Index: i + 1, World_X_mm: v.x, World_Y_mm: v.y, World_Z_mm: v.z, VBR_Height_mm: upper === 'VBR' ? 0 : null, Frame_Index: frame });
    });
    rows.push({ ...base, Category: 'Plane', Name: 'center', Index: null, World_X_mm: c.x, World_Y_mm: c.y, World_Z_mm: c.z, Frame_Index: frame });
    rows.push({ ...base, Category: 'Plane', Name: 'normal_unit', Index: null, World_X_mm: n.x, World_Y_mm: n.y, World_Z_mm: n.z, Frame_Index: frame });
    rows.push({ ...base, Category: 'Point', Name: 'center', Index: null, World_X_mm: c.x, World_Y_mm: c.y, World_Z_mm: c.z, Height_to_VBR_mm: upper === 'VBR' ? 0 : null, Frame_Index: frame });
    return rows;
  }

  /** The AI rows with the live landmarks, the refit VBR ring, the re-sliced contours and the
   * current margins applied, and the pipeline's derived metrics of recomputed structures
   * removed so the renderer measures the geometry instead. */
  rows(): MeasurementRow[] {
    const rows = this.aiRows.map((r) => ({ ...r }));
    for (const l of this.landmarks.values()) {
      rows[l.rowIndex] = { ...rows[l.rowIndex], World_X_mm: l.live.x, World_Y_mm: l.live.y, World_Z_mm: l.live.z };
    }
    let out: MeasurementRow[] = rows;
    if (this.vbrRefit) {
      const fit = this.vbrRefit;
      const template = rows.find((r) => r.Category === 'RingPoint' && r.Structure === 'VBR');
      out = this.replaceRing(out, 'VBR', template?.Frame_Index ?? null, fit.ring, fit.center, fit.normal);
    }
    for (const c of this.contours.values()) {
      if (!c.ring) continue;
      const m = measureRing(c.ring);
      out = this.replaceRing(out, c.structure, c.frame, c.ring, m?.centroid ?? c.center, c.normal);
    }
    for (const [s, pts] of this.margins) {
      const template = out.find((r) => r.Category === 'CurvePoint' && r.Structure === s);
      if (!template) continue;
      out = out.filter((r) => !(r.Structure === s && (r.Category === 'CurvePoint' || (r.Category === 'Metric' && r.Name === 'length'))));
      pts.forEach((p, i) => {
        const v = toVecMm(p);
        out.push({ ...template, Index: i + 1, World_X_mm: v.x, World_Y_mm: v.y, World_Z_mm: v.z });
      });
    }
    // heights and lengths derived from moved landmarks are recomputed by the renderer
    const movedStructures = new Set<string>();
    for (const l of this.landmarks.values()) if (this.isMovedFromAi(l.def.key)) movedStructures.add(l.def.structure);
    if (movedStructures.size) {
      out = out.filter((r) => {
        if (r.Category === 'Distance' && (r.Structure === 'Intercommissural' || r.Structure === 'CoaptationToCommissure') && (movedStructures.has('Commissure') || movedStructures.has('Coaptation'))) return false;
        if (r.Category === 'Angle' && r.Structure === 'Commissural' && movedStructures.has('Commissure')) return false;
        return true;
      });
    }
    return out;
  }

  // ------------------------------------------------------------ save
  private buildCurrent(label: string | null): CorrectionSnapshot {
    const points: PointEdit[] = [];
    for (const l of this.landmarks.values()) points.push({ identity: l.identity, frame: l.frame, original: l.ai, edited: l.live });
    const centerRowEdit = (structure: string, frame: number | null, ai: THREE.Vector3, now: THREE.Vector3) => {
      const row = this.aiRows.find((r) => r.Category === 'Point' && r.Structure.toUpperCase() === structure.toUpperCase() && r.Name === 'center');
      if (row && typeof row.World_X_mm === 'number') points.push({ identity: rowIdentity(row), frame: row.Frame_Index ?? frame, original: { x: row.World_X_mm, y: row.World_Y_mm!, z: row.World_Z_mm! }, edited: toVecMm(now) });
      else points.push({ identity: { category: 'Point', structure, name: 'center', index: 0 }, frame, original: toVecMm(ai), edited: toVecMm(now) });
    };
    if (this.vbrRefit) {
      const pred = parseMeasurements(this.aiRows, this.configuration);
      centerRowEdit('VBR', null, pred.planes.get('VBR')?.center ?? this.vbrRefit.center, this.vbrRefit.center);
    }
    for (const c of this.contours.values()) {
      if (this.contourAtAi(c)) continue;
      c.handles.forEach((h, i) => points.push({ identity: { category: null, structure: c.structure, name: `${c.def.prefix}_${i + 1}`, index: 0 }, frame: null, original: toVecMm(c.aiHandles[i]), edited: toVecMm(h) }));
      points.push({ identity: { category: null, structure: c.structure, name: c.def.centerName, index: 0 }, frame: null, original: toVecMm(c.aiCenterHandle), edited: toVecMm(c.centerHandle) });
      centerRowEdit(c.structure, c.frame, c.aiCenter, c.center);
    }
    const reading = (this.configuration ?? '').toLowerCase();
    const margins: { structure: string; configuration: string | null; points: THREE.Vector3[] }[] = [];
    for (const s of MARGIN_STRUCTURES) {
      const pts = this.margins.get(s) ?? this.aiMargins.get(s);
      if (pts) margins.push({ structure: s, configuration: reading || null, points: pts });
    }
    const otherReadings = (this.loaded?.free_margins ?? []).filter((m) => (m.configuration ?? '').toLowerCase() !== reading && m.configuration);
    // a nadir edit rebuilds the Root Centroid, dropping any loaded hand edits of the centerline
    const centerline = this.rootCentroid ? [] : (this.loaded?.corrections ?? []).filter((e) => e.structure === 'CenterLine');
    const snapshot = buildSnapshot({
      runId: this.runId,
      projectId: this.projectId,
      label,
      points,
      margins,
      centerline,
      customMetrics: this.loaded?.custom_metrics ?? [],
      carried: this.carried.filter((e) => e.structure !== 'CenterLine'),
    });
    for (const m of otherReadings) snapshot.free_margins.push(m);
    return snapshot;
  }

  /** Save: overwrite the active version (keeps its label). Without one, call saveAs. */
  async save(): Promise<void> {
    if (!this.activeSlug) return;
    const label = this.versions.find((v) => v.slug === this.activeSlug)?.label ?? this.activeSlug;
    await this.write(this.activeSlug, label);
  }

  /** Save As: slug from the label; an existing slug is overwritten silently (desktop rule). */
  async saveAs(label: string): Promise<boolean> {
    const slug = slugify(label);
    if (!slug) return false;
    await this.write(slug, label.trim());
    return true;
  }

  private async write(slug: string, label: string): Promise<void> {
    this.busy = true;
    this.emit();
    try {
      const snapshot = this.buildCurrent(label);
      const res = await correctionsApi.put(this.runId, slug, snapshot);
      this.loaded = res.snapshot;
      this.activeSlug = slug;
      this.dirty = false;
      // RebaselineAllToCurrent: the saved state becomes the reset baseline
      for (const l of this.landmarks.values()) l.baseline = { ...l.live };
      for (const c of this.contours.values()) {
        c.baselineHandles = c.handles.map((p) => p.clone());
        c.baselineCenter = c.center.clone();
        c.baselineNormal = c.normal.clone();
      }
      this.marginBaseline.clear();
      for (const [s, pts] of this.margins) this.marginBaseline.set(s, pts.map((p) => p.clone()));
      this.carried = [];
      this.skippedCount = 0;
      this.versions = (await correctionsApi.list(this.runId)).versions;
      this.error = null;
    } catch (e) {
      this.error = (e as Error).message;
    }
    this.busy = false;
    this.emit();
  }

  async deleteActive(): Promise<void> {
    if (!this.activeSlug) return;
    this.busy = true;
    this.emit();
    try {
      await correctionsApi.delete(this.runId, this.activeSlug);
      this.versions = this.versions.filter((v) => v.slug !== this.activeSlug);
      this.applyLoaded(null, null);
      this.error = null;
    } catch (e) {
      this.error = (e as Error).message;
    }
    this.busy = false;
    this.emit();
  }
}

function dist(a: Vec3Mm, b: Vec3Mm): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}
